// =============================================================================
// src/data/analytics-overlay.ts — Composição do patch vivo do Analytics.
//
// Padrão sentinel-overlay: builders puros dashboard→patch; o patch só carrega
// campos com dado real (campo ausente = consumidor mantém o mock). KPIs e
// comparação substituem SOMENTE as posições cobertas pela coleta viva
// (menções/interações); alcance e sentimento seguem mock com tag
// "demonstração" (decisão 2026-07-24 — backlog F3.1/F3.2).
// 2026-09-02: os números somam o RECORTE ativo (escopo × período × reações)
// a partir da diária por escopo (migration 025) — analytics-overlay-scope.
// =============================================================================

import { ANALYTICS_DEFAULT_DASHBOARD_OPTIONS, type AnalyticsDashboardFetchOptions, type AnalyticsLiveDashboard } from './analytics-client';
import type {
  AnalyticsActorPlatformRow,
  AnalyticsIngestionGraphqlData,
  AnalyticsMentionsDailyRow,
  AnalyticsRegionWindowRow,
  AnalyticsWatchWindowRow,
} from './analytics-live-query';
import type {
  AnalyticsLivePatch,
} from '../features/analytics/analytics-live-store';
import {
  ANALYTICS_KPI_INTERACTIONS_LABEL,
  ANALYTICS_KPI_MENTIONS_LABEL,
  type OmarAnalyticsComparisonMetric,
  type OmarAnalyticsKpi,
  type OmarAnalyticsPlatformCount,
  type OmarAnalyticsWindowSlice,
} from '../features/analytics/analytics-types';
import {
  OMAR_ANALYTICS_COMPARISON_METRICS,
  OMAR_ANALYTICS_KPIS,
  OMAR_ANALYTICS_PLATFORM_COUNTS,
} from '../features/analytics/analytics-mock-data';
import {
  buildAnalyticsFeedPatch,
  buildAnalyticsInfluencersPatch,
  buildAnalyticsSitesPatch,
} from './analytics-overlay-feed';
import {
  analyticsOutletScopeLabelByHandle,
  buildAnalyticsIngestionPatch,
} from './analytics-overlay-ingestion';
import {
  analyticsPeriodLabelFor,
  analyticsPeriodWindowDays,
  analyticsScopeKpiSubtitle,
  analyticsScopedDailyRows,
  buildAnalyticsFreshnessPatch,
} from './analytics-overlay-scope';
import {
  analyticsRowsInCurrentWindow,
  analyticsWindowTotals,
  buildAnalyticsChartSeriesPatch,
} from './analytics-overlay-series';
import { analyticsPlatformUiLabelFor, analyticsPtBrCompactNumber, analyticsPtBrInteger } from './analytics-overlay-format';

/** Janela padrão dos KPIs/comparação (recorte last30) — o período da UI
 * sobrescreve via AnalyticsKpiWindow desde 2026-09-02. */
const ANALYTICS_KPI_WINDOW_DAYS = 30;

/** Recorte que os KPIs/lede/rail somam: dias da janela + rótulos explicativos. */
export interface AnalyticsKpiWindow {
  readonly windowDays: number;
  readonly periodLabel: string;
  readonly scopeSubtitle: string;
}

/** Janela para o recorte pedido ao dashboard (default = Núcleo, 30d, sem reações). */
export function analyticsKpiWindowFor(view: AnalyticsDashboardFetchOptions = ANALYTICS_DEFAULT_DASHBOARD_OPTIONS): AnalyticsKpiWindow {
  return {
    windowDays: analyticsPeriodWindowDays(view.period),
    periodLabel: analyticsPeriodLabelFor(view.period),
    scopeSubtitle: analyticsScopeKpiSubtitle(view),
  };
}

const DEFAULT_KPI_WINDOW: AnalyticsKpiWindow = analyticsKpiWindowFor();

/** Diária que os números somam: por escopo (025) quando veio; senão a do cenário
 * inteiro (fixtures/backend antigo). Recorte com ZERO linhas soma zero — nunca
 * cai no cenário inteiro em silêncio. */
export function analyticsDailyRowsForView(dashboard: AnalyticsLiveDashboard): readonly AnalyticsMentionsDailyRow[] {
  const scopedRows = dashboard.mentionsDailyScoped ?? [];
  if (scopedRows.length === 0) return dashboard.mentionsDaily;
  return analyticsScopedDailyRows(scopedRows, dashboard.view ?? ANALYTICS_DEFAULT_DASHBOARD_OPTIONS);
}

/**
 * Monta o patch vivo completo; dashboard vazio → {} (mock integral).
 *
 * @example applyAnalyticsLivePatch(buildAnalyticsLivePatch(await client.fetchAnalyticsDashboard()));
 */
export function buildAnalyticsLivePatch(
  dashboard: AnalyticsLiveDashboard,
  ingestion: AnalyticsIngestionGraphqlData | null = null,
): AnalyticsLivePatch {
  const patch: Record<string, unknown> = {};
  const kpiWindow = analyticsKpiWindowFor(dashboard.view);
  const dailyRows = analyticsDailyRowsForView(dashboard);
  // Gate = HOUVE coleta (cenário inteiro); o recorte ativo pode somar zero e
  // ainda assim é um número real, não motivo para voltar ao mock.
  const hasCollection = dashboard.mentionsDaily.length > 0 || (dashboard.mentionsDailyScoped ?? []).length > 0;
  const chartSeries = buildAnalyticsChartSeriesPatch(dailyRows);
  const feedItems = buildAnalyticsFeedPatch(dashboard.feedBuckets);
  const windowTotals = analyticsWindowTotals(dailyRows, kpiWindow.windowDays, 0);
  const influencers = buildAnalyticsInfluencersPatch(dashboard.authorWindow, windowTotals.mentions);
  const freshness = buildAnalyticsFreshnessPatch(dashboard.ingestionFreshness ?? [], dashboard.fetchedAtMs ?? 0);
  if (freshness) patch['freshness'] = freshness;
  const outletScopeByHandle = analyticsOutletScopeLabelByHandle(ingestion?.outletScopes ?? []);
  const sites = buildAnalyticsSitesPatch(dashboard.newsSourceDaily, outletScopeByHandle);
  if (chartSeries) patch['chartSeries'] = chartSeries;
  if (feedItems) patch['feedItems'] = feedItems;
  // Série com dado + feed vazio = recorte legítimo sem conteúdo (o filtro de
  // escopo 017/018 pode zerar um canal). Marca explicitamente para o store
  // registrar o vazio; sem isto a chave sumia e a UI mostrava o mock em silêncio.
  else if (hasCollection) patch['feedItems'] = [];
  if (influencers) patch['influencers'] = influencers;
  if (sites) patch['sites'] = sites;
  const actorTotals = buildAnalyticsActorTotalsPatch(dashboard.actorPlatformWindow);
  if (actorTotals) patch['actorTotals'] = actorTotals;
  const themeWindow = buildAnalyticsThemeWindowPatch(dashboard.watchWindow);
  if (themeWindow) patch['themeWindow'] = themeWindow;
  const regionWindow = buildAnalyticsRegionWindowPatch(dashboard.regionWindow);
  if (regionWindow) patch['regionWindow'] = regionWindow;
  const ingestionConfig = ingestion ? buildAnalyticsIngestionPatch(ingestion) : undefined;
  if (ingestionConfig) patch['ingestionConfig'] = ingestionConfig;
  if (hasCollection) {
    patch['kpis'] = buildAnalyticsKpisPatch(dailyRows, dashboard.regionWindow, kpiWindow);
    patch['platformCounts'] = buildAnalyticsPlatformCountsPatch(dailyRows, kpiWindow.windowDays);
    patch['comparisonMetrics'] = buildAnalyticsComparisonPatch(dailyRows, kpiWindow.windowDays);
    patch['ledeTotals'] = buildAnalyticsLedeTotalsPatch(dailyRows, kpiWindow);
  }
  return patch as AnalyticsLivePatch;
}

/** Temas 30d → fatias com share do total de menções tematizadas (aba Análises). */
export function buildAnalyticsThemeWindowPatch(
  watchRows: readonly AnalyticsWatchWindowRow[],
): readonly OmarAnalyticsWindowSlice[] | undefined {
  if (watchRows.length === 0) return undefined;
  const totalMentions = watchRows.reduce((sum, watchRow) => sum + watchRow.mentions, 0);
  return watchRows.map((watchRow) => ({
    sliceLabel: watchRow.theme_label,
    sliceDetail: `${analyticsPtBrCompactNumber(watchRow.interactions)} interacoes`,
    mentions: watchRow.mentions,
    sharePct: totalMentions > 0 ? Math.round((watchRow.mentions / totalMentions) * 100) : 0,
  }));
}

/** Regiões citadas 30d → fatias (share sobre a região mais citada, p/ barras). */
export function buildAnalyticsRegionWindowPatch(
  regionRows: readonly AnalyticsRegionWindowRow[],
): readonly OmarAnalyticsWindowSlice[] | undefined {
  if (regionRows.length === 0) return undefined;
  const topMentions = regionRows.reduce((max, regionRow) => Math.max(max, regionRow.mentions), 0);
  return regionRows.map((regionRow) => ({
    sliceLabel: regionRow.region_name,
    sliceDetail: regionRow.region_kind === 'state' ? 'estado' : `municipio${regionRow.uf ? ` · ${regionRow.uf}` : ''}`,
    mentions: regionRow.mentions,
    sharePct: topMentions > 0 ? Math.round((regionRow.mentions / topMentions) * 100) : 0,
  }));
}

/** Strip 100% real no live (2026-07-25, "remover os conteudos mockados"): os
 * KPIs mock de alcance/sentimento (que so existirao na F3) dao lugar a views
 * de video e municipios citados — ambos medidos na base. */
export function buildAnalyticsKpisPatch(
  dailyRows: readonly AnalyticsMentionsDailyRow[],
  regionRows: readonly AnalyticsRegionWindowRow[] = [],
  kpiWindow: AnalyticsKpiWindow = DEFAULT_KPI_WINDOW,
): readonly OmarAnalyticsKpi[] {
  const current = analyticsWindowTotals(dailyRows, kpiWindow.windowDays, 0);
  const previous = analyticsWindowTotals(dailyRows, kpiWindow.windowDays, 1);
  const citedMunicipalities = regionRows.filter((regionRow) => regionRow.region_kind === 'municipality').length;
  return OMAR_ANALYTICS_KPIS.map((mockKpi) => {
    if (mockKpi.label === ANALYTICS_KPI_MENTIONS_LABEL) {
      // Subtítulo explica o que o número conta ("nucleo do cenario AM, sem reacoes"):
      // ao excluir reações e contexto ele cai bastante frente aos relatórios diários.
      return { ...mockKpi, value: analyticsPtBrInteger(current.mentions), ...analyticsKpiDelta(current.mentions, previous.mentions), context: `${kpiWindow.scopeSubtitle} · ${kpiWindow.periodLabel}` };
    }
    if (mockKpi.label === ANALYTICS_KPI_INTERACTIONS_LABEL) {
      return { ...mockKpi, value: analyticsPtBrCompactNumber(current.interactions), ...analyticsKpiDelta(current.interactions, previous.interactions), context: 'likes, comentarios e shares ao vivo' };
    }
    if (mockKpi.label === 'Alcance estimado') {
      return { label: 'Views de video', value: analyticsPtBrCompactNumber(current.views), ...analyticsKpiDelta(current.views, previous.views), context: `TikTok e Reels, ${kpiWindow.periodLabel}` };
    }
    return { label: 'Municipios citados', value: analyticsPtBrInteger(citedMunicipalities), delta: 'no texto das mencoes', trend: 'flat' as const, context: 'gazetteer do AM, ultimos 30 dias' };
  });
}

function analyticsKpiDelta(currentTotal: number, previousTotal: number): { readonly delta: string; readonly trend: OmarAnalyticsKpi['trend'] } {
  if (previousTotal <= 0) return { delta: 'sem base anterior', trend: 'flat' };
  const deltaPct = Math.round(((currentTotal - previousTotal) / previousTotal) * 100);
  if (deltaPct === 0) return { delta: 'estavel', trend: 'flat' };
  return { delta: `${deltaPct > 0 ? '+' : ''}${deltaPct}%`, trend: deltaPct > 0 ? 'up' : 'down' };
}

/** Mantém as 8 linhas fixas do rail; contagem 0 para canal sem coleta (ex.: Blogs).
 * Janela = MESMOS 30d dos KPIs/lede (fix 2026-07-25: usava a janela cheia de
 * 120d das séries e as contagens divergiam do total de menções do topo). */
export function buildAnalyticsPlatformCountsPatch(
  dailyRows: readonly AnalyticsMentionsDailyRow[],
  windowDays: number = ANALYTICS_KPI_WINDOW_DAYS,
): readonly OmarAnalyticsPlatformCount[] {
  const windowRows = analyticsRowsInCurrentWindow(dailyRows, windowDays);
  const totalsByLabel = new Map<string, number>();
  let allMentions = 0;
  for (const dailyRow of windowRows) {
    const platformLabel = analyticsPlatformUiLabelFor(dailyRow.platform, dailyRow.content_kind);
    totalsByLabel.set(platformLabel, (totalsByLabel.get(platformLabel) ?? 0) + dailyRow.mentions);
    allMentions += dailyRow.mentions;
  }
  return OMAR_ANALYTICS_PLATFORM_COUNTS.map((mockPlatformCount) => ({
    ...mockPlatformCount,
    count: mockPlatformCount.platform === 'Todos' ? allMentions : totalsByLabel.get(mockPlatformCount.platform) ?? 0,
  }));
}

/** Substitui só o card "Mencoes" (janela atual vs anterior); demais seguem mock (F3). */
export function buildAnalyticsComparisonPatch(
  dailyRows: readonly AnalyticsMentionsDailyRow[],
  windowDays: number = ANALYTICS_KPI_WINDOW_DAYS,
): readonly OmarAnalyticsComparisonMetric[] {
  const current = analyticsWindowTotals(dailyRows, windowDays, 0);
  const previous = analyticsWindowTotals(dailyRows, windowDays, 1);
  return OMAR_ANALYTICS_COMPARISON_METRICS.map((mockMetric) => {
    if (mockMetric.metricName === 'Mencoes') {
      return { ...mockMetric, currentValue: analyticsPtBrInteger(current.mentions), previousValue: analyticsPtBrInteger(previous.mentions), deltaLabel: analyticsComparisonDeltaLabel(current.mentions, previous.mentions) };
    }
    if (mockMetric.metricName === 'Interacoes') {
      return { ...mockMetric, currentValue: analyticsPtBrCompactNumber(current.interactions), previousValue: analyticsPtBrCompactNumber(previous.interactions), deltaLabel: analyticsComparisonDeltaLabel(current.interactions, previous.interactions) };
    }
    return mockMetric;
  });
}

function analyticsComparisonDeltaLabel(currentTotal: number, previousTotal: number): string {
  const delta = currentTotal - previousTotal;
  return `${delta >= 0 ? '+' : '-'}${analyticsPtBrCompactNumber(Math.abs(delta))}`;
}

/** Totais REAIS por personagem (janela 30d de content_actor_match) — chips. */
export function buildAnalyticsActorTotalsPatch(
  actorRows: readonly AnalyticsActorPlatformRow[],
): AnalyticsLivePatch['actorTotals'] | undefined {
  if (actorRows.length === 0) return undefined;
  const byActor = new Map<string, { actorSlug: string; actorName: string; totalMentions: number; mentionsByPlatformLabel: Record<string, number> }>();
  for (const actorRow of actorRows) {
    const actorTotal = byActor.get(actorRow.actor_slug)
      ?? { actorSlug: actorRow.actor_slug, actorName: actorRow.actor_name, totalMentions: 0, mentionsByPlatformLabel: {} };
    const platformLabel = analyticsPlatformUiLabelFor(actorRow.platform, actorRow.platform === 'web' ? 'news' : 'post');
    actorTotal.totalMentions += actorRow.mentions;
    actorTotal.mentionsByPlatformLabel[platformLabel] = (actorTotal.mentionsByPlatformLabel[platformLabel] ?? 0) + actorRow.mentions;
    byActor.set(actorRow.actor_slug, actorTotal);
  }
  return [...byActor.values()].sort((left, right) => right.totalMentions - left.totalMentions);
}

function buildAnalyticsLedeTotalsPatch(
  dailyRows: readonly AnalyticsMentionsDailyRow[],
  kpiWindow: AnalyticsKpiWindow,
): AnalyticsLivePatch['ledeTotals'] {
  const current = analyticsWindowTotals(dailyRows, kpiWindow.windowDays, 0);
  return { mentionsLabel: analyticsPtBrInteger(current.mentions), periodLabel: kpiWindow.periodLabel };
}
